// All tool definitions with categories, icons, colors, and descriptions

export const TOOL_CATEGORIES = {
  organize: {
    id: 'organize',
    label: 'Organize PDF',
    color: '#3374ff',
    bgLight: 'rgba(51,116,255,0.08)',
    bgMedium: 'rgba(51,116,255,0.15)',
    icon: 'Layers',
  },
  optimize: {
    id: 'optimize',
    label: 'Optimize PDF',
    color: '#10b981',
    bgLight: 'rgba(16,185,129,0.08)',
    bgMedium: 'rgba(16,185,129,0.15)',
    icon: 'Zap',
  },
  convertTo: {
    id: 'convertTo',
    label: 'Convert to PDF',
    color: '#f9603e',
    bgLight: 'rgba(249,96,62,0.08)',
    bgMedium: 'rgba(249,96,62,0.15)',
    icon: 'ArrowRightCircle',
  },
  convertFrom: {
    id: 'convertFrom',
    label: 'Convert from PDF',
    color: '#8b5cf6',
    bgLight: 'rgba(139,92,246,0.08)',
    bgMedium: 'rgba(139,92,246,0.15)',
    icon: 'ArrowLeftCircle',
  },
  edit: {
    id: 'edit',
    label: 'Edit PDF',
    color: '#ec4899',
    bgLight: 'rgba(236,72,153,0.08)',
    bgMedium: 'rgba(236,72,153,0.15)',
    icon: 'Pencil',
  },
  security: {
    id: 'security',
    label: 'PDF Security',
    color: '#ef4444',
    bgLight: 'rgba(239,68,68,0.08)',
    bgMedium: 'rgba(239,68,68,0.15)',
    icon: 'Shield',
  },
  image: {
    id: 'image',
    label: 'Image Tools',
    color: '#06b6d4',
    bgLight: 'rgba(6,182,212,0.08)',
    bgMedium: 'rgba(6,182,212,0.15)',
    icon: 'Image',
  },
  document: {
    id: 'document',
    label: 'Document Tools',
    color: '#f59e0b',
    bgLight: 'rgba(245,158,11,0.08)',
    bgMedium: 'rgba(245,158,11,0.15)',
    icon: 'FileText',
  },
};

export const TOOLS = [
  // Organize PDF
  { id: 'merge-pdf', name: 'Merge PDF', desc: 'Combine multiple PDFs into one document', category: 'organize', icon: 'Combine', popular: true },
  { id: 'split-pdf', name: 'Split PDF', desc: 'Separate a PDF into individual pages or sections', category: 'organize', icon: 'Scissors' },
  { id: 'remove-pages', name: 'Remove Pages', desc: 'Delete specific pages from your PDF', category: 'organize', icon: 'Trash2' },
  { id: 'extract-pages', name: 'Extract Pages', desc: 'Pull specific pages out of a PDF', category: 'organize', icon: 'FileOutput' },
  { id: 'organize-pdf', name: 'Organize PDF', desc: 'Reorder, rotate and manage PDF pages', category: 'organize', icon: 'LayoutGrid' },
  { id: 'scan-to-pdf', name: 'Scan to PDF', desc: 'Capture documents with your camera', category: 'organize', icon: 'ScanLine' },

  // Optimize PDF
  { id: 'compress-pdf', name: 'Compress PDF', desc: 'Reduce file size without losing quality', category: 'optimize', icon: 'Minimize2', popular: true },
  { id: 'repair-pdf', name: 'Repair PDF', desc: 'Fix corrupted or damaged PDF files', category: 'optimize', icon: 'Wrench' },
  { id: 'ocr-pdf', name: 'OCR PDF', desc: 'Make scanned PDFs searchable with text recognition', category: 'optimize', icon: 'Eye' },
  { id: 'flatten-pdf', name: 'Flatten PDF', desc: 'Flatten form fields and annotations', category: 'optimize', icon: 'Square' },

  // Convert to PDF
  { id: 'word-to-pdf', name: 'Word to PDF', desc: 'Convert DOCX and DOC files to PDF', category: 'convertTo', icon: 'FileType', popular: true },
  { id: 'excel-to-pdf', name: 'Excel to PDF', desc: 'Transform spreadsheets into PDF documents', category: 'convertTo', icon: 'Table' },
  { id: 'ppt-to-pdf', name: 'PowerPoint to PDF', desc: 'Convert presentations to PDF format', category: 'convertTo', icon: 'Presentation' },
  { id: 'jpg-to-pdf', name: 'JPG to PDF', desc: 'Convert images to PDF documents', category: 'convertTo', icon: 'ImagePlus' },
  { id: 'html-to-pdf', name: 'HTML to PDF', desc: 'Convert web pages and HTML to PDF', category: 'convertTo', icon: 'Globe' },
  { id: 'txt-to-pdf', name: 'Text to PDF', desc: 'Convert plain text files to PDF', category: 'convertTo', icon: 'Type' },

  // Convert from PDF
  { id: 'pdf-to-word', name: 'PDF to Word', desc: 'Convert PDF documents to editable DOCX', category: 'convertFrom', icon: 'FileText', popular: true },
  { id: 'pdf-to-excel', name: 'PDF to Excel', desc: 'Extract tables and data to spreadsheets', category: 'convertFrom', icon: 'Sheet' },
  { id: 'pdf-to-ppt', name: 'PDF to PowerPoint', desc: 'Convert PDFs to editable presentations', category: 'convertFrom', icon: 'Monitor' },
  { id: 'pdf-to-jpg', name: 'PDF to JPG', desc: 'Convert PDF pages to high-quality images', category: 'convertFrom', icon: 'Image' },
  { id: 'pdf-to-pdfa', name: 'PDF to PDF/A', desc: 'Convert to archival standard format', category: 'convertFrom', icon: 'Archive' },
  { id: 'pdf-to-txt', name: 'PDF to Text', desc: 'Extract all text content from PDF', category: 'convertFrom', icon: 'AlignLeft' },

  // Edit PDF
  { id: 'edit-pdf', name: 'Edit PDF', desc: 'Modify text, images and content in PDFs', category: 'edit', icon: 'PenTool' },
  { id: 'rotate-pdf', name: 'Rotate PDF', desc: 'Rotate pages to the correct orientation', category: 'edit', icon: 'RotateCcw' },
  { id: 'add-page-numbers', name: 'Page Numbers', desc: 'Add page numbers to your document', category: 'edit', icon: 'Hash' },
  { id: 'add-watermark', name: 'Add Watermark', desc: 'Stamp text or image watermarks on PDFs', category: 'edit', icon: 'Droplets' },
  { id: 'crop-pdf', name: 'Crop PDF', desc: 'Trim PDF page margins and borders', category: 'edit', icon: 'Crop' },
  { id: 'annotate-pdf', name: 'Annotate PDF', desc: 'Add comments, highlights, and notes', category: 'edit', icon: 'MessageSquare' },

  // PDF Security
  { id: 'sign-pdf', name: 'Sign PDF', desc: 'Add your digital signature to documents', category: 'security', icon: 'PenLine', popular: true },
  { id: 'unlock-pdf', name: 'Unlock PDF', desc: 'Remove password protection from PDF', category: 'security', icon: 'Unlock' },
  { id: 'protect-pdf', name: 'Protect PDF', desc: 'Add password encryption to your PDF', category: 'security', icon: 'Lock' },
  { id: 'redact-pdf', name: 'Redact PDF', desc: 'Permanently hide sensitive information', category: 'security', icon: 'EyeOff' },
  { id: 'compare-pdf', name: 'Compare PDF', desc: 'Side-by-side diff of two documents', category: 'security', icon: 'GitCompare' },

  // Image Tools
  { id: 'bg-remove', name: 'Remove Background', desc: 'AI-powered background removal from images', category: 'image', icon: 'Eraser', popular: true },
  { id: 'image-resize', name: 'Resize Image', desc: 'Resize and scale images to any dimension', category: 'image', icon: 'Maximize2' },
  { id: 'image-compress', name: 'Compress Image', desc: 'Reduce image file size efficiently', category: 'image', icon: 'Minimize' },
  { id: 'image-convert', name: 'Convert Image', desc: 'Convert between JPG, PNG, WEBP, SVG', category: 'image', icon: 'RefreshCw' },
  { id: 'image-crop', name: 'Crop Image', desc: 'Crop and adjust image framing', category: 'image', icon: 'Crop' },
  { id: 'image-enhance', name: 'Enhance Image', desc: 'AI-powered image quality enhancement', category: 'image', icon: 'Sparkles' },

  // Document Tools
  { id: 'doc-merge', name: 'Merge Documents', desc: 'Combine Word, Excel, or mixed documents', category: 'document', icon: 'FolderPlus' },
  { id: 'email-pdf', name: 'Email Document', desc: 'Send documents directly via email', category: 'document', icon: 'Mail' },
  { id: 'cloud-save', name: 'Save to Cloud', desc: 'Export to Google Drive, Dropbox, OneDrive', category: 'document', icon: 'Cloud' },
  { id: 'batch-convert', name: 'Batch Convert', desc: 'Convert multiple files at once', category: 'document', icon: 'Layers' },
  { id: 'template-fill', name: 'Fill Template', desc: 'Auto-fill document templates with data', category: 'document', icon: 'ClipboardList' },
];

export const POPULAR_TOOLS = TOOLS.filter(t => t.popular);

export const getToolsByCategory = (categoryId) =>
  TOOLS.filter(t => t.category === categoryId);

export const getToolById = (id) =>
  TOOLS.find(t => t.id === id);

export const searchTools = (query) => {
  const q = query.toLowerCase();
  return TOOLS.filter(t =>
    t.name.toLowerCase().includes(q) ||
    t.desc.toLowerCase().includes(q) ||
    t.category.toLowerCase().includes(q)
  );
};

export const INTEGRATIONS = [
  { id: 'google-drive', name: 'Google Drive', icon: '🔵', color: '#4285F4' },
  { id: 'dropbox', name: 'Dropbox', icon: '🔷', color: '#0061FF' },
  { id: 'onedrive', name: 'OneDrive', icon: '☁️', color: '#0078D4' },
  { id: 'icloud', name: 'iCloud', icon: '🌤️', color: '#3693F3' },
  { id: 'box', name: 'Box', icon: '📦', color: '#0061D5' },
];

export const PRICING_PLANS = [
  {
    id: 'free',
    name: 'Free',
    price: 0,
    period: 'forever',
    desc: 'Perfect for occasional use',
    features: [
      '5 documents per month',
      'Basic conversions',
      'Standard compression',
      '10MB max file size',
      'Community support',
    ],
    cta: 'Get Started Free',
    popular: false,
  },
  {
    id: 'pro',
    name: 'Pro',
    price: 9,
    period: 'per month',
    desc: 'For professionals who need more',
    features: [
      'Unlimited documents',
      'All conversion formats',
      'Advanced compression',
      '500MB max file size',
      'OCR & batch processing',
      'Priority email support',
      'Cloud storage integrations',
      'No watermarks',
    ],
    cta: 'Start Pro Trial',
    popular: true,
  },
  {
    id: 'business',
    name: 'Business',
    price: 24,
    period: 'per user/month',
    desc: 'For teams and organizations',
    features: [
      'Everything in Pro',
      'Unlimited file size',
      'Team workspaces',
      'Admin dashboard',
      'API access',
      'Custom branding',
      'SSO & SAML',
      'Dedicated support',
      'Audit logs',
    ],
    cta: 'Contact Sales',
    popular: false,
  },
];
